<template>
  <v-form ref="form" @submit.prevent>
    <FormSection>
      <BaseHeading :level="2">{{ $t(`${translationTicketPath}.engine.damage`) }}</BaseHeading>
    </FormSection>

    <FormSection v-for="n in componentsCount" :key="n" :class="$style.component">
      <IconSelect
        v-if="canRemoveCurrentComponent"
        :class="$style.remove"
        icon="substract-circle"
        :title="$t('complaintForm.claim.remove')"
        @click.native="removeComponent(n - 1)"
      />

      <BaseSelect
        v-model="formData.component[n - 1]"
        class="mb-6"
        :error-messages="chooseErrors('formData.component', n - 1)"
        :hide-details="!chooseErrors('formData.component', n - 1).length"
        :label="$t('complaintForm.claim.component_label')"
        :items="componentItems"
        required
        v-on="getTouchListeners('formData.component', n - 1)"
        @change="resetSubsequentSelects('component', n - 1)"
      />

      <BaseSelect
        v-if="formData.component[n - 1]"
        v-model="formData.errorPart[n - 1]"
        class="mb-6"
        :error-messages="chooseErrors('formData.errorPart', n - 1)"
        :hide-details="!chooseErrors('formData.errorPart', n - 1).length"
        :label="$t('complaintForm.claim.error_part_label')"
        :items="errorPartItems(formData.component[n - 1])"
        required
        v-on="getTouchListeners('formData.errorPart', n - 1)"
        @change="resetSubsequentSelects('errorPart', n - 1)"
      />

      <BaseSelect
        v-if="isErrorTypeDirty(n - 1) || formData.errorPart[n - 1]"
        v-model="formData.errorType[n - 1]"
        class="mb-6"
        :error-messages="chooseErrors('formData.errorType', n - 1)"
        :hide-details="!chooseErrors('formData.errorType', n - 1).length"
        :label="$t('complaintForm.claim.error_type_label')"
        :items="errorTypeItems(formData.errorPart[n - 1])"
        :disabled="!formData.errorPart[n - 1]"
        required
        v-on="getTouchListeners('formData.errorType', n - 1)"
      />

      <template v-if="isErrorTypeDirty(n - 1) || formData.errorType[n - 1]">
        <BaseTextarea
          v-model="formData.description[n - 1]"
          rows="15"
          class="mb-6"
          :error-messages="requiredErrors('formData.description', n - 1)"
          :hide-details="!requiredErrors('formData.description', n - 1).length"
          :label="$t('complaintForm.claim.description')"
          counter
          required
          v-on="getTouchListeners('formData.description', n - 1)"
        />

        <v-row>
          <v-col class="py-0">
            <BaseHeading :level="3" class="mb-1 required">{{
              $t('complaintForm.claim.file_upload')
            }}</BaseHeading>

            <FileDropzone
              v-model="dropzoneFiles[n - 1]"
              class="mb-6"
              :error-messages="fileUploadErrors(n - 1)"
              :hide-details="!fileUploadErrors(n - 1).length"
              :full-width="true"
              :destroy-dropzone="false"
              url="/api/bureturnapi?code=image-upload"
              required
              :options="{ createImageThumbnails: false, destroyDropzone: false }"
              @[onDropzoneSuccess]="res => onFileUploadSuccess(res, n - 1)"
              @[onDropzoneSending]="setPending(true)"
              @[onDropzoneComplete]="setPending(false)"
              @[onDropzoneRemovedFile]="onFileUploadRemovedFile(n - 1)"
            />
          </v-col>
        </v-row>
      </template>
    </FormSection>

    <BaseFieldset :disabled="!canAddNextComponent || !isValid">
      <div
        :class="[$style.pointer, $style['selection--none']]"
        class="justify-center d-flex flex-column align-center"
        @click="addComponent"
      >
        <IconSelect
          :class="$style.add"
          icon="add-circle"
          :height="40"
          :width="40"
          :title="$t('complaintForm.claim.add')"
        />

        <span :class="[$style.info]" class="mt-3">{{ $t('complaintForm.claim.add') }}</span>
      </div>
    </BaseFieldset>

    <ComplaintFormStepperControls
      :is-valid="isValid"
      :has-required-fields="true"
      :prev-step="prevStep"
      :next-step="submit"
    />
  </v-form>
</template>

<script>
/** TODO: Make image thumbnails work */
/** -------------------------------- */
import { entries, get, isEmpty, compact } from 'lodash-es'
import { mapMutations } from 'vuex'

import { required } from 'vuelidate/lib/validators'

import StepperMixin from '@/js/mixins/stepper-mixin.js'
import FormValidationMixin from '@/js/components/Forms/mixins/form-validation-mixin.js'
import ComplaintFormMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-default-mixin.js'

import FORM_CONSTANTS from '@/js/components/Forms/ComplaintForm/_store-module/constants.js'
import FORM_MUTATION from '@/js/components/Forms/ComplaintForm/_store-module/mutation-types.js'

import FileDropzone, { FILE_DROPZONE_EVENTS } from '@/js/components/Shared/FileDropzone.vue'
import IconSelect from '@/js/components/Shared/IconSelect/IconSelect.vue'

import FormSection from '@/js/components/Forms/FormSection.vue'

import ComplaintFormStepperControls from '../../ComplaintFormStepperControls.vue'

export default {
  name: 'ComplaintFormComponentErrorDesc',
  components: {
    IconSelect,
    FormSection,
    FileDropzone,
    ComplaintFormStepperControls,
  },
  mixins: [FormValidationMixin, ComplaintFormMixin],
  props: {
    ...StepperMixin.props,
  },
  data() {
    return {
      onDropzoneSuccess: FILE_DROPZONE_EVENTS.VDROPZONE_SUCCESS,
      onDropzoneSending: FILE_DROPZONE_EVENTS.VDROPZONE_SENDING,
      onDropzoneComplete: FILE_DROPZONE_EVENTS.VDROPZONE_COMPLETE,
      onDropzoneRemovedFile: FILE_DROPZONE_EVENTS.VDROPZONE_REMOVED_FILE,
      minComponentsCount: 1,
      componentsCount: 1,
      dropzoneFiles: [],
      dropzoneErrors: [],
      formData: {
        component: [],
        errorPart: [],
        errorType: [],
        description: [],
        fileUpload: [],
      },
    }
  },
  validations: {
    formData: {
      component: {
        $each: {
          required,
        },
      },
      errorPart: {
        $each: {
          required,
        },
      },
      errorType: {
        $each: {
          required,
        },
      },
      description: {
        $each: {
          required,
        },
      },
      fileUpload: {
        $each: {
          required,
        },
      },
    },
  },
  computed: {
    /** Computed helper */
    componentToPartTranslations() {
      return this.getMappedTranslations(
        'complaintForm.claim.map_component_to_part',
        'complaintForm.claim.part',
      )
    },
    partToKindTranslations() {
      return this.getMappedTranslations(
        'complaintForm.claim.map_part_to_old_kind',
        'complaintForm.claim.old_kind',
      )
    },
    canAddNextComponent() {
      const index = this.componentsCount - 1

      return entries(this.formData).every(([, value]) => {
        const currentItemValue = get(value, index)

        return !isEmpty(currentItemValue) || currentItemValue === 1
      })
    },
    canRemoveCurrentComponent() {
      return this.componentsCount > this.minComponentsCount
    },
    /** Form data */
    fullFormData() {
      const claim = []

      for (let index = 0; index < this.componentsCount; index++) {
        const component = {
          place: this.$t(`complaintForm.claim.old_component.${this.formData.component[index]}`),
          part: this.$t(`complaintForm.claim.part.${this.formData.errorPart[index]}`),
          kind: this.$t(`complaintForm.claim.old_kind.${this.formData.errorType[index]}`),
          fileUpload: this.formData.fileUpload[index],
          description: this.formData.description[index],
        }

        claim.push(component)
      }

      return { claim }
    },
    /** Select items */
    componentItems() {
      return entries(this.$t('complaintForm.claim.old_component')).map(([key, value]) => ({
        text: value,
        value: key,
      }))
    },
  },
  mounted() {
    Object.keys(this.formData).forEach(key => {
      for (let index = 0; index < this.minComponentsCount; index++) {
        this.formData[key].push(undefined)
      }
    })
  },
  methods: {
    ...mapMutations(FORM_CONSTANTS.STORE_ID, {
      updateFormData: FORM_MUTATION.UPDATE_FORM_DATA,
      setPending: FORM_MUTATION.SET_FORM_PENDING,
    }),
    submitCallback() {
      this.updateFormData(this.fullFormData)
      this.nextStep()
    },
    getMappedTranslations(mappingPath, translationPath) {
      const translations = {}
      const componentToPartMapping = this.$t(mappingPath)

      entries(componentToPartMapping).forEach(([key, value]) => {
        translations[key] = value
          .replace(/\[|\]/g, '')
          .split(',')
          .map(translationIndex => [
            translationIndex,
            this.$t(`${translationPath}.${translationIndex}`),
          ])
      })

      return translations
    },
    addComponent() {
      if (this.canAddNextComponent) {
        this.componentsCount++

        Object.keys(this.formData).forEach(key => {
          this.formData[key].push(undefined)
        })

        this.resetValidation()
      }
    },
    removeComponent(index) {
      if (index > -1) {
        entries(this.formData).forEach(([, value]) => {
          value.splice(index, 1)
        })

        this.componentsCount--
      }
    },
    resetSubsequentSelects(currentSelect, index) {
      switch (currentSelect) {
        case 'component':
          this.formData.errorPart.splice(index, 1, undefined)
          this.formData.errorType.splice(index, 1, undefined)
          break
        case 'errorPart':
          this.formData.errorType.splice(index, 1, undefined)
          break
        default:
          break
      }
    },
    isErrorTypeDirty(index) {
      return get(this.$v, `formData.errorType.$each.$iter.${index}.$dirty`, false)
    },
    onFileUploadSuccess({ response }, index) {
      const fileUploadPath = get(response, 'files.0')

      if (fileUploadPath) {
        this.formData.fileUpload.splice(index, 1, fileUploadPath)
      } else {
        this.formData.fileUpload.splice(index, 1)
      }

      const fileUploadError = get(response, 'error')
        ? this.$t('form.dropzone.uploadFailed')
        : undefined

      if (fileUploadError) {
        this.dropzoneErrors.splice(index, 1, fileUploadError)
      } else {
        this.dropzoneErrors.splice(index, 1)
      }
    },
    onFileUploadRemovedFile(index) {
      if (index < this.componentsCount) {
        this.formData.fileUpload.splice(index, 1, undefined)
        this.dropzoneErrors.splice(index, 1)
      }
    },
    /** Select items */
    errorPartItems(index) {
      return get(this.componentToPartTranslations, index, []).map(([key, value]) => ({
        text: value,
        value: key,
      }))
    },
    errorTypeItems(index) {
      return get(this.partToKindTranslations, index, []).map(([key, value]) => ({
        text: value,
        value: key,
      }))
    },
    /** Errors */
    chooseErrors(input, index) {
      return this.formErrorHelper(input, [this.chooseError], index)
    },
    requiredErrors(input, index) {
      return this.formErrorHelper(input, [this.requiredError], index)
    },
    fileUploadErrors(index) {
      return compact([
        get(this.fileUploadsErrors, index),
        ...this.formErrorHelper(
          'formData.fileUpload',
          [
            {
              condition: 'required',
              error: this.fileErrorMessage,
            },
          ],
          index,
        ),
      ])
    },
  },
}
</script>

<style lang="scss" module>
.component {
  position: relative;

  padding: 2.5rem 1.5rem;

  border: 1px solid var(--grey-lighten-1);
}

.selection--none {
  user-select: none;
}

.pointer,
.add,
.remove {
  cursor: pointer;
}

.add {
  display: block;
  margin: auto;

  color: var(--primary);
}

.remove {
  position: absolute;
  top: -1.25rem;
  right: 0.75rem;

  box-sizing: content-box;
  padding: 0.5rem 0.75rem;

  color: var(--red);

  background: var(--white);
}

.info {
  color: var(--grey);
}
</style>
